import { useState } from 'react';
import type { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';
import {
  AreaFactsTable,
  CoverageSummaryBar,
  GeoAddressModal,
  GtmNoscript,
  jsonLdScripts,
  LOCAL_CONSENT_SCRIPTS,
  LOCAL_FAVICON_LINKS,
  LOCAL_FONT_LINKS,
  LocalBreadcrumb,
  LocalTopbar,
  PLAN_RIBBONS,
  PlanCard,
  planTermFact,
  RangePanel,
  SEO_LOCAL_CSS,
  SeoLocalIcon,
  ZipCard,
  ZipGapCard,
} from 'components/SeoGeoLocal';
import type { GeoModalPlan, PlanCardFact } from 'components/SeoGeoLocal';
import type { CityView } from 'components/SeoGeoLocal/cityViews';
import { cartQualifyUrl } from 'utils/seoGeo/cartLink';
import { formatIsoDate, formatPlanPrice } from 'utils/seoGeo/format';
import { localPagesEnabled } from 'utils/seoGeo/pagesEnabled';
import {
  displayPlanName,
  isStreamingPlan,
  planPriceParts,
  planSubtitle,
} from 'utils/seoGeo/planDisplay';
import type { DisplayPlan } from 'utils/seoGeo/planDisplay';

// LUM-492: ONE reviewed template renders every Xfinity city page — pages
// are data. This is the city-scale twin of the neighborhood [area] route
// (LUM-553/ADR-021). The CITIES allowlist and its validated views live in
// components/SeoGeoLocal/cityViews (LUM-592): this module carries NO data
// imports, so a city page's browser bundle ships only its own view, passed
// through getStaticProps props. The registry still validates EVERY
// allowlisted entry when the build first imports it, and
// registry-validation.test.ts runs the same guards as a named suite.
//
// Existence discipline is the same as the neighborhood route: a city joins
// the allowlist only via a reviewed diff (targets entry + ingest run +
// one CITIES line), never derived from targets.json — a probe-only target
// can't mint a route. Philadelphia is the only entry today; Houston keeps
// its literal scaffold page (it shadows this dynamic route in Next) until
// it graduates in a later wave PR.
//
// Address-modal POC: this page ships the Next runtime so plan CTAs can open
// the address modal. CTAs stay real anchors into the funnel and are only
// upgraded on click; where /provider/_next assets don't resolve (the apex
// worker route today), the page degrades to pure link-out (LUM-501).

// Brand values follow the XfinitySpecial microsite config
// (src/config/microsites/XfinitySpecial.tsx) — copied as literals so the
// static page doesn't pull the funnel's provider-config import graph into
// its bundle. Keep in sync with the config and the neighborhood route.
const BBNL_URL = 'https://www.xfinity.com/broadband-labels';
const HOMEPAGE = 'https://xfinityspecial.com';
// Same-origin /provider/* asset: resolves on localhost and the Pages
// preview, 404s on the apex today — the /local/* worker route doesn't serve
// /provider/* paths. Deferred WITH the runtime-JS question (LUM-501).
const LOGO_SRC = '/provider/themes/XfinityTheme/logo.svg';
const LOGO_ALT = 'Xfinity Authorized Dealer';
const PRIVACY_URL = 'https://updater.com/uhs-terms/';

function planCardFacts(view: CityView, entry: DisplayPlan): PlanCardFact[] {
  return [
    {
      icon: 'check',
      text: `Available in ${entry.plan.zipCount} of ${view.snapshot.coverage.totalZips} ZIP codes we track`,
    },
    planTermFact(entry),
  ];
}

interface CityLocalPageProps {
  state: string;
  city: string;
  /** ISO date baked at build time, so the static HTML and its hydration can
   * never disagree about the claim dates. */
  pageUpdatedAt: string;
  /** The city's own validated view, built in getStaticProps from its
   * committed data — the ONLY data this page ships (LUM-592). jest suites
   * render this page directly and pass the registry's view explicitly. */
  view: CityView;
}

export default function CityLocalPage({
  pageUpdatedAt,
  view,
}: CityLocalPageProps) {
  const { snapshot } = view;
  // null = closed; { plan: null } = open without plan context (hero/final CTA).
  const [modal, setModal] = useState<{ plan: GeoModalPlan | null } | null>(
    null
  );
  return (
    <>
      <Head>
        <title>{view.pageTitle}</title>
        <meta name="description" content={view.pageDescription} />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href={view.canonical} />
        {LOCAL_FAVICON_LINKS}
        {LOCAL_FONT_LINKS}
        {LOCAL_CONSENT_SCRIPTS}
        {/* serializeJsonLd escapes "<"; values are committed snapshot data. */}
        {jsonLdScripts(view.jsonLd)}
      </Head>
      {/* header/main/footer must be SIBLINGS: nested inside <main> they lose
          their banner/contentinfo landmark roles (WCAG 1.3.1), so the brand
          chrome and legal content would vanish from landmark navigation. The
          .seo-local scope lives on this wrapper div instead. */}
      <div data-testid={`local-${view.citySegment}-page`} className="seo-local">
        <GtmNoscript />
        {/* React HTML-escapes text children of <style>, and browsers never
            decode entities inside raw-text elements — SEO_LOCAL_CSS must stay
            free of the escapable characters (regression-tested). */}
        <style>{SEO_LOCAL_CSS}</style>
        <a className="skipLink" href="#geo-main">
          Skip to content
        </a>
        <LocalTopbar
          homeHref={HOMEPAGE}
          logoSrc={LOGO_SRC}
          logoAlt={LOGO_ALT}
          phone={view.phone}
        />

        <main id="geo-main">
          <div className="wrap">
            <LocalBreadcrumb items={view.breadcrumbItems} />

            <div className="hero">
              <span className="eyebrow">
                <SeoLocalIcon name="mapPin" />
                {`${view.cityName}, ${view.stateAbbr} · ${snapshot.coverage.totalZips} ZIP codes tracked`}
              </span>
              <h1>{`Xfinity internet in ${view.cityName}, ${view.stateName}`}</h1>
              <p className="lede" data-testid="answer-lead">
                {`Xfinity internet plans in ${view.cityName} currently run from $${view.priceRange.min} to $${view.priceRange.max} a month. In our latest check, plans could be ordered in ${view.effective.coverage.availableZips} of the ${view.effective.coverage.totalZips} ${view.cityName} ZIP codes we track. Your exact price and plan lineup depend on your address.`}
              </p>
              <RangePanel
                headline={`Xfinity is available in ${view.cityName}`}
                stats={[
                  { k: 'Speeds available', v: view.speedRangeLabel },
                  {
                    k: 'Price range',
                    v: (
                      <>
                        {`$${view.priceRange.min} – $${view.priceRange.max}`}
                        <small>/mo*</small>
                      </>
                    ),
                  },
                  { k: 'Plans', v: `${snapshot.plans.length} available` },
                ]}
                primaryCta={{
                  href: view.cartUrl,
                  label: 'Check availability at your address',
                }}
                secondaryCta={{ href: '#plans', label: 'Compare plans below' }}
                note="Offers and prices are not guaranteed and may vary. Check your address to confirm your exact price. No account needed."
                onPrimaryClick={() => setModal({ plan: null })}
              />
            </div>

            <section id="plans">
              <div className="sectionHead">
                <h2>{`Current Xfinity plans in ${view.cityName}`}</h2>
                <p className="sectionSub">
                  {`Current prices for ${
                    view.cityName
                  }, verified against the live ordering system.${
                    view.displayPlans.some(
                      (entry) => planPriceParts(entry.plan).from
                    )
                      ? ' Where a plan has two contract terms we show the lowest, marked "from".'
                      : ''
                  } Promotions vary by address, so check yours for the exact price and current offers.`}
                </p>
              </div>
              <div className="plans" data-testid="plans-grid">
                {view.displayPlans.map((entry) => (
                  <PlanCard
                    key={entry.plan.planName}
                    ribbon={entry.ribbon ? PLAN_RIBBONS[entry.ribbon] : null}
                    name={displayPlanName(entry.plan.planName)}
                    subtitle={planSubtitle(entry.plan.planName)}
                    subtitleIcon={
                      isStreamingPlan(entry.plan.planName)
                        ? 'televisionSimple'
                        : 'wifiHigh'
                    }
                    speedMbps={entry.speedMbps}
                    price={planPriceParts(entry.plan)}
                    facts={planCardFacts(view, entry)}
                    bbnl={{ href: BBNL_URL, label: 'See broadband facts' }}
                    cta={{ href: view.cartUrl, label: 'Check availability' }}
                    onCtaClick={() =>
                      setModal({
                        plan: {
                          name: displayPlanName(entry.plan.planName),
                          priceLabel: formatPlanPrice(entry.plan),
                        },
                      })
                    }
                  />
                ))}
              </div>
            </section>

            <section id="coverage">
              <div className="sectionHead">
                <h2>{`Xfinity availability by ${view.cityName} ZIP code`}</h2>
                <p className="sectionSub">
                  Where Xfinity plans could and could not be ordered in our
                  latest check. We show the gaps too, so you are never guessing.
                </p>
              </div>
              <CoverageSummaryBar
                label={
                  <>
                    {`${view.effective.coverage.pct}% `}
                    {/* The sourcing qualifier lives in the SAME node as the
                        figure (the FCC same-node rule): the plan cards say
                        "8 of 9 ZIP codes we track" from the postal probe, and
                        this bar says 9 of 9 — without the inline qualifier the
                        identical phrase carries two different numerators on
                        one page. */}
                    <small>{`· available in ${
                      view.effective.coverage.availableZips
                    } of ${
                      view.effective.coverage.totalZips
                    } ZIP codes we track${
                      view.effective.overriddenZips.length > 0
                        ? ` (${view.effective.overriddenZips.length} verified at street addresses)`
                        : ''
                    }`}</small>
                  </>
                }
                fillPct={view.effective.coverage.pct}
              />
              <div className="zips">
                <div className="contents" data-testid="orderable-zips">
                  {view.effectiveOrderableZips.map((zip) =>
                    view.effective.overriddenZips.indexOf(zip) !== -1 ? (
                      // Street-address-verified ZIP (LUM-562): available on
                      // the strength of the committed override, so the card
                      // cites that sourcing and makes NO plan-count claim —
                      // address-level lookups return a different catalog shape
                      // than the metro snapshot.
                      <ZipCard
                        key={zip}
                        zip={zip}
                        note="Verified at street addresses in this ZIP"
                        label={view.zipLabels?.[zip]}
                      />
                    ) : (
                      // Label falls back to the SP7 area name for orderable
                      // ZIPs without a shipped page — the card stays UNLINKED
                      // per the dead-link rule; only liveNeighborhoodPages
                      // entries carry an href.
                      <ZipCard
                        key={zip}
                        zip={zip}
                        note={view.orderableZipNote}
                        label={
                          view.liveNeighborhoodPages[zip]?.label ??
                          view.zipLabels?.[zip]
                        }
                        href={view.liveNeighborhoodPages[zip]?.href}
                      />
                    )
                  )}
                </div>
                <div className="contents" data-testid="coverage-gap">
                  {view.effective.unavailableZips.map((zip) => (
                    <ZipGapCard
                      key={zip}
                      zip={zip}
                      label={view.zipLabels?.[zip]}
                      note={
                        <>
                          No Xfinity plans came back as orderable here{' '}
                          <b>yet</b>. We would rather tell you now than after
                          you type your address. We re-check daily and update
                          this page when that changes.
                        </>
                      }
                    />
                  ))}
                </div>
              </div>
              {view.effective.overriddenZips.length > 0 && (
                <p className="sectionSub" data-testid="override-note">
                  {`Our automated ZIP-level check still reports ${view.overriddenZipLabels.join(
                    ', '
                  )} as unavailable, but Xfinity orders went through at street addresses we sampled there, so we list it as available. We re-verify those addresses monthly. Check your own address to confirm.`}
                </p>
              )}
            </section>

            <section id="neighborhoods" data-testid="area-facts">
              <div className="sectionHead">
                <h2>{`${view.cityName} neighborhoods at a glance`}</h2>
                <p className="sectionSub">
                  {`What the Census says about the ZIP code areas we track. ZIP boundaries don't line up exactly with neighborhood lines, so each row describes the area around a ZIP. Broadband figures count households with a broadband subscription from any provider. They are not Xfinity coverage figures.`}
                </p>
              </div>
              <AreaFactsTable
                headers={{
                  area: 'Area',
                  population: 'Population',
                  housingMix: 'Housing mix',
                  renters: 'Renters',
                  broadband: 'Homes with broadband',
                }}
                rows={view.areaRows}
                source={
                  <>
                    {'Source: '}
                    <a href="https://www.census.gov/programs-surveys/acs">
                      {`U.S. Census Bureau, ${view.acsRelease}`}
                    </a>
                    {'. Estimates carry margins of error.'}
                  </>
                }
              />
            </section>

            <section id="fcc">
              <div className="sectionHead">
                <h2>{`Broadband coverage across ${view.cityName}`}</h2>
              </div>
              {/* Two independent FCC facts, never multiplied: res_st_pct uses
                the FCC's residential location base as its denominator, while
                totalUnits counts ALL serviceable locations — the bases differ,
                so a computed covered-location count would be a false claim.
                See FccProviderSummary in utils/seoGeo/types.ts. */}
              <div data-testid="fcc-facts">
                <div className="factsGrid">
                  <div className="panel bigStat">
                    <div className="v num">{view.fccUnits}</div>
                    <div className="k">
                      {`broadband-serviceable locations in ${view.cityName}`}
                    </div>
                    <div className="src">
                      {'Source: '}
                      <a href="https://broadbandmap.fcc.gov/">
                        FCC National Broadband Map
                      </a>
                    </div>
                  </div>
                  <div className="panel">
                    <h3>Provider-reported residential coverage</h3>
                    <p className="panelSub">
                      Share of the FCC residential location base where Comcast,
                      the parent company of Xfinity, reports fixed service at
                      any technology or speed.
                    </p>
                    <div className="bar me">
                      <div className="bl">
                        <span className="bn me">Comcast (Xfinity)</span>
                        {/* Qualifier lives in the SAME element as the number:
                          snippet/AI extraction grabs single nodes, and a bare
                          "99.6%" beside the FCC source invites the fused
                          misreading this section exists to prevent. */}
                        <span className="bv num">
                          {view.comcastPct}
                          <small> provider-reported</small>
                        </span>
                      </div>
                      <div className="btrack">
                        <span
                          className="bfill"
                          style={{ width: view.comcastPct }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="caveat">
                  <SeoLocalIcon name="info" />
                  <div className="ct">
                    {`The availability above reflects what could actually be ordered. The FCC tracks something different: where providers report service. As of ${view.fccAsOf}, the FCC counts ${view.fccUnits} broadband-serviceable locations in ${view.cityName}. Provider-reported coverage does not guarantee that a plan can be ordered at a specific address.`}
                  </div>
                </div>
              </div>
            </section>

            <section id="check">
              <div className="finalCta">
                <h2>{`See your exact ${view.cityName} offer`}</h2>
                <p>
                  Check your address to see the precise price, promotions, and
                  availability you can order today.
                </p>
                <a
                  className="btn primary"
                  href={view.cartUrl}
                  onClick={(event) => {
                    event.preventDefault();
                    setModal({ plan: null });
                  }}
                >
                  Check availability at your address{' '}
                  <SeoLocalIcon name="arrowRight" />
                </a>
              </div>
            </section>

            {/* Matt's launch protection (Slack, Jul 29): the footnote the
                price asterisks point at, kept close to his wording. */}
            <p className="disclaimer" data-testid="price-disclaimer">
              *Offers and prices are not guaranteed and may vary. Search your
              address for the most accurate pricing and offers available at your
              address.
            </p>
          </div>
        </main>

        <footer>
          <div className="wrap foot">
            <div className="legal">
              {/* Year is build-baked (from pageUpdatedAt) so the static
                  export can't disagree with its own hydration. */}
              {`© ${pageUpdatedAt.slice(
                0,
                4
              )} Updater, Inc. All rights reserved `}
              <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>
            </div>
            <p data-testid="provenance">
              {`Plan and price data comes from the live Xfinity ordering system and is re-checked daily; when a check finds a change, we review and update this page. Prices shown are ranges for ${
                view.cityName
              } and vary by address and contract term. Coverage figures: FCC National Broadband Map, data as of ${
                view.fccAsOf
              }. Neighborhood area facts: U.S. Census Bureau, ${
                view.acsRelease
              }. Page updated ${formatIsoDate(pageUpdatedAt)}.`}
            </p>
            <div>
              {'Broadband facts and FCC labels: '}
              <a href={BBNL_URL}>xfinity.com/broadband-labels</a>
            </div>
            <div>
              {/* Root-relative on purpose: resolves on the preview and the
                  apex where these pages serve; dev's /provider basePath misses
                  it (dev-only nuisance). Pages link up, the index links down —
                  the crawl loop that de-orphans the section (LUM-543). */}
              {'More locations: '}
              <a href="/local">every city we track</a>
            </div>
            <div>
              xfinityspecial.com is an authorized reseller. Xfinity is a
              trademark of Comcast.
            </div>
          </div>
        </footer>

        {modal && (
          <GeoAddressModal
            plan={modal.plan}
            cartUrl={view.cartUrl}
            cartUrlForAddress={(address) =>
              cartQualifyUrl(view.promoId, address)
            }
            cityLabel={view.cityName}
            onClose={() => setModal(null)}
          />
        )}
      </div>
    </>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  // Dynamic import on purpose: Next strips getStaticPaths/getStaticProps
  // from browser bundles, so the registry (every city's data) stays
  // server-side (LUM-592). Imported BEFORE the gate check so the registry's
  // module-scope validation fails ANY build — gated environments included —
  // exactly like the old top-level imports did.
  const { CITIES: registry } = await import('components/SeoGeoLocal/cityViews');
  // Gated environments generate no city pages at all (LUM-501 launch gate).
  if (!localPagesEnabled()) {
    return { paths: [], fallback: false };
  }
  return {
    paths: Object.keys(registry).map((key) => ({
      params: {
        state: registry[key].stateSegment,
        city: registry[key].citySegment,
      },
    })),
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps<CityLocalPageProps> = async ({
  params,
}) => {
  // Registry first, gate second — validation must not depend on the flag.
  const { CITY_VIEWS } = await import('components/SeoGeoLocal/cityViews');
  if (!localPagesEnabled()) {
    return { notFound: true };
  }
  const state = params?.state;
  const city = params?.city;
  if (
    typeof state !== 'string' ||
    typeof city !== 'string' ||
    !CITY_VIEWS[`${state}/${city}`]
  ) {
    throw new Error(
      `getStaticProps: "${String(state)}/${String(
        city
      )}" is not an allowlisted city`
    );
  }
  return {
    props: {
      state,
      city,
      pageUpdatedAt: new Date().toISOString().slice(0, 10),
      // JSON round-trip strips optional-and-absent fields (e.g. a city
      // without zipLabels) — Next refuses undefined in props.
      view: JSON.parse(
        JSON.stringify(CITY_VIEWS[`${state}/${city}`])
      ) as CityView,
    },
  };
};
